import React from 'react';
import { Link } from 'react-router-dom';
import type { LucideIcon } from 'lucide-react';
import { Button } from '../src/components/ui/button';

/**
 * The signed-out invitation shown on an authenticated-only page.
 *
 * COVERS: the three routes behind ProtectedRoute — `/favourites`, `/account`,
 * `/account/saved-searches`: 3 of 4,177 built routes. It renders the CARD ONLY.
 * Each page supplies its own wrapper, because their layouts genuinely differ —
 * favourites is a fixed full-bleed pane below the header, the other two centre in
 * the viewport — and reproducing favourites' exact wrapper here would have
 * silently changed a screen the operator ruled unchanged.
 *
 * DOES NOT COVER: opening the sign-in dialog, or remembering where the visitor
 * was headed. Each page already does that itself via
 * `openAuthModal({ returnUrl })` and deliberately keeps it, because AccountPage
 * additionally distinguishes a DELIBERATE sign-out (which goes home) from an
 * unauthenticated arrival (which invites signing in). A shared component cannot
 * know that difference, so it does not try to.
 *
 * SIBLING: `ProtectedRoute` waits for auth to resolve and nothing else — it does
 * NOT render this, and is not a substitute for a page handling its own signed-out
 * state. A page added under ProtectedRoute that renders neither this nor its own
 * signed-out branch will show its signed-in UI to a signed-out visitor.
 *
 * ⚠️ WHY THIS EXISTS AT ALL, because the history is the point: until 2026-08-14
 * ProtectedRoute redirected all three routes to '/' before they could mount, so
 * all three pages' signed-out handling was unreachable — FavouritesPage's
 * perfectly good sign-in screen among it, and two pages' PERMANENT SPINNERS
 * (AccountPage's labelled "Loading your account" while loading nothing). Removing
 * that redirect is what made this screen reachable. Do not reintroduce a redirect
 * here to "simplify": the redirect was the defect (item 4).
 *
 * Wording is operator-ruled (2026-08-14), per page, favourites unchanged.
 */
export interface SignedOutPromptProps {
    /** Page-appropriate icon; matches the icon that page already uses elsewhere. */
    icon: LucideIcon;
    /** Operator-ruled heading, e.g. "Sign in to view favourites". */
    heading: string;
    /** Operator-ruled body, e.g. "Create an account to save your favourite gyms." */
    body: string;
}

const SignedOutPrompt: React.FC<SignedOutPromptProps> = ({ icon: Icon, heading, body }) => (
    <div className="text-center">
        <Icon className="w-16 h-16 text-border-default mx-auto mb-6" />
        <h2 className="text-2xl font-semibold text-text-primary mb-3">{heading}</h2>
        <p className="text-text-muted mb-6">{body}</p>
        {/* UI-39: <Button asChild> (primary variant supplies the previous
            bg-brand-primary/text-white/hover colours; border-0 cancels the variant's
            1px transparent border so the box size is unchanged; text-base preserves
            the Link's inherited 16px over the Button base's text-sm; base inline-flex
            replaces inline-block — identical box for this single text child). Router
            <Link> kept: in-app navigation, not a plain anchor. Only accepted delta:
            harmonised white focus ring. Moved here verbatim from FavouritesPage when
            the three signed-out screens were consolidated (item 4). */}
        <Button asChild className="border-0 px-6 py-3 rounded-lg text-base font-semibold">
            <Link to="/auth">Sign in</Link>
        </Button>
    </div>
);

export default SignedOutPrompt;
